<template>
  <div
    class="message-text__wrap"
    :class="{
      'show--quoted': isQuotedContentPresent,
      'hide--quoted': !isQuotedContentPresent,
    }"
    @click="onInlineImageClick"
  >
<div
  v-if="isDraftActivity"
  class="text-content flex items-center gap-2 flex-wrap"
>
  <p>{{ formattedDraftMessage }} </p>
  <woot-button
    variant="smooth"
    color-scheme="primary"
    size="small"
    class="!px-2 !py-0.5 text-xs rounded-lg"
    @click="viewDraft"
  >
    {{ $t('CONVERSATION.DRAFT_ACTIVITY.VIEW_DRAFT') }}
     <fluent-icon icon="eye-show" size="14" />
  </woot-button>
</div>
    <div v-else-if="!isEmail" v-dompurify-html="message" class="text-content" />
    <letter v-else class="text-content" :html="message" />
    <button
      v-if="showQuoteToggle"
      class="text-slate-300 dark:text-slate-300 cursor-pointer text-xs py-1"
      @click="toggleQuotedContent"
    >
      <span v-if="showQuotedContent" class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-up" size="16" />
        {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
      </span>
      <span v-else class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-down" size="16" />
        {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
      </span>
    </button>
    <woot-modal
      v-if="showImagePreview"
      :full-width="true"
      :show="showImagePreview"
      :on-close="closeImagePreview"
    >
      <img
        :src="previewImageUrl"
        class="modal-image skip-context-menu max-h-[76vh] max-w-[76vw]"
        alt=""
      />
    </woot-modal>
  </div>
</template>

<script>
import Letter from 'vue-letter';

const MIN_PREVIEWABLE_IMAGE_SIZE = 40;

export default {
  components: { Letter },
  props: {
    message: {
      type: String,
      default: '',
    },
    isEmail: {
      type: Boolean,
      default: true,
    },
    displayQuotedButton: {
      type: Boolean,
      default: false,
    },
    contentAttributes: {
      type: Object,
      default: () => ({}),
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      showQuotedContent: false,
      showImagePreview: false,
      previewImageUrl: '',
    };
  },
  computed: {
    isQuotedContentPresent() {
      if (!this.isEmail) {
        return this.message.includes('<blockquote');
      }
      return this.showQuotedContent;
    },
    showQuoteToggle() {
      if (!this.isEmail) {
        return false;
      }
      return this.displayQuotedButton;
    },
    isDraftActivity() {
      return this.contentAttributes && this.contentAttributes.draft_id;
    },
    draftId() {
      return this.contentAttributes?.draft_id;
    },
    formattedDraftMessage() {
      if (!this.isDraftActivity) return '';
      
      const scheduledAt = this.contentAttributes?.draft_scheduled_at;
      if (!scheduledAt) return 'Draft scheduled';
      
      // Format the date in user's timezone, matching DraftViewer format
      const date = new Date(scheduledAt);
      const formattedDate = date.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      
      return `Email Draft scheduled for ${formattedDate}`;
    },
  },
  methods: {
    toggleQuotedContent() {
      this.showQuotedContent = !this.showQuotedContent;
    },
    viewDraft() {
      if (this.draftId && this.conversationId) {
        this.$router.push({
          name: 'scheduled_drafts',
          params: {
            accountId: this.$route.params.accountId,
            conversation_id: this.conversationId,
          },
          query: { 
            draft_id: this.draftId,
            from_activity: 'true' // Indicate we came from an activity message
          },
        });
      }
    },
    isPreviewableInlineImage(target) {
      if (!target || target.tagName !== 'IMG') {
        return false;
      }
      if (!target.closest('.text-content')) {
        return false;
      }
      const src = target.currentSrc || target.src;
      if (!src) {
        return false;
      }
      const { naturalWidth, naturalHeight } = target;
      if (
        (naturalWidth > 0 && naturalWidth < MIN_PREVIEWABLE_IMAGE_SIZE) ||
        (naturalHeight > 0 && naturalHeight < MIN_PREVIEWABLE_IMAGE_SIZE)
      ) {
        return false;
      }
      return true;
    },
    onInlineImageClick(event) {
      const { target } = event;
      if (!this.isPreviewableInlineImage(target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.previewImageUrl = target.currentSrc || target.src;
      this.showImagePreview = true;
    },
    closeImagePreview() {
      this.showImagePreview = false;
      this.previewImageUrl = '';
    },
  },
};
</script>
<style lang="scss">
.text-content {
  overflow: auto;

  img {
    cursor: pointer;
  }

  ul,
  ol {
    padding-left: var(--space-two);
  }
  table {
    margin: 0;
    border: 0;

    td {
      margin: 0;
      border: 0;
    }

    tr {
      border-bottom: 0 !important;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: var(--font-size-normal);
  }
  p {
    font-weight: 500;
  }
}

.show--quoted {
  blockquote {
    @apply block;
  }
}

.hide--quoted {
  blockquote {
    @apply hidden;
  }
}
</style>
